import { API_DATA_PERMISSION_REQUEST_STATUSES } from "@repo/shared-primitives"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { PageHeader, Toolbar } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { RequestsTabs } from "@/features/api-requests-admin/RequestsTabs"
import { SegmentedFilter } from "@/features/api-requests-admin/SegmentedFilter"
import { LISTING_PROPOSAL_STATUS_LABELS } from "@/features/integration-app-listing-proposals/listing-proposal-labels"
import { PermissionRequestsTable } from "@/features/integration-app-scope-requests/PermissionRequestsTable"
import { usePermissionRequests } from "@/queries/marketplace"

/** `all` lifts the status filter; an absent `status` means the pending backlog. */
const STATUS_FILTERS = [...API_DATA_PERMISSION_REQUEST_STATUSES, "all"] as const
type StatusFilter = (typeof STATUS_FILTERS)[number]

const DEFAULT_STATUS: StatusFilter = "pending"
const PAGE_SIZE = 50

export const Route = createFileRoute("/integration-apps/permission-requests/")({
	component: PermissionRequestsPage,
	validateSearch: (search: Record<string, unknown>) => ({
		page: normalizePage(search.page),
		status: STATUS_FILTERS.find((status) => status === search.status),
	}),
})

function normalizePage(raw: unknown): number | undefined {
	const page = Math.floor(Number(raw))
	return Number.isFinite(page) && page > 1 ? page : undefined
}

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
	...API_DATA_PERMISSION_REQUEST_STATUSES.map((status) => ({
		value: status,
		label: LISTING_PROPOSAL_STATUS_LABELS[status],
	})),
	{ value: "all", label: "All" },
]

/** Backlog of permission and price data requests from apps and accounts, pending first. */
function PermissionRequestsPage() {
	const { page = 1, status = DEFAULT_STATUS } = useSearch({
		from: "/integration-apps/permission-requests/",
	})
	const navigate = useNavigate()

	const { data, isLoading } = usePermissionRequests({
		status,
		limit: PAGE_SIZE,
		offset: (page - 1) * PAGE_SIZE,
	})
	const total = data?.total ?? 0
	const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))

	const goTo = (next: { page?: number; status?: StatusFilter }) =>
		navigate({
			to: "/integration-apps/permission-requests",
			search: {
				page: next.page && next.page > 1 ? next.page : undefined,
				status:
					(next.status ?? status) === DEFAULT_STATUS
						? undefined
						: (next.status ?? status),
			},
			replace: true,
		})

	return (
		<div className="flex flex-col gap-4 p-6">
			<PageHeader
				title="Requests"
				description="Permission sets and price data that apps and accounts asked for; nothing changes until you accept"
			/>
			<RequestsTabs />

			<Toolbar>
				<SegmentedFilter
					label="Status"
					options={STATUS_OPTIONS}
					value={status}
					onChange={(next) => goTo({ status: next })}
				/>
			</Toolbar>

			<PermissionRequestsTable rows={data?.rows} isLoading={isLoading} />

			{total > PAGE_SIZE && (
				<div className="flex items-center justify-end gap-3 text-xs text-meta">
					<span className="tabular-nums">
						Page {page} of {lastPage}
					</span>
					<Button
						size="sm"
						variant="outline"
						disabled={page <= 1}
						onClick={() => goTo({ page: page - 1 })}
					>
						Previous
					</Button>
					<Button
						size="sm"
						variant="outline"
						disabled={page >= lastPage}
						onClick={() => goTo({ page: page + 1 })}
					>
						Next
					</Button>
				</div>
			)}
		</div>
	)
}
